import { useAuth } from '@shared/providers/AuthProvider';
import { Facet, getFacets } from '@shared/services/search-service';
import { QueryOptions, useQueries } from '@tanstack/react-query';

type ParentChildMap = Record<string, Record<string, string[]>>;
type FlatStructure = Record<string, string[]>;

type UseGetFacetsResult = {
    parentChildMap: ParentChildMap;
    flatStructure: FlatStructure;
    isLoading: boolean;
};

// Helper function to convert hierarchical data into a parent-child map
/**
 * Converts hierarchical facet data into a parent-child map.
 *
 * Example Input:
 * [
 *   {
 *     name: "makes",
 *     value: "Ford",
 *     score: 149,
 *     children: [
 *       { name: "models", value: "Fiesta", score: 10 },
 *       { name: "models", value: "F150", score: 11, }
 *     ]
 *   },
 *   {
 *     name: "states",
 *     value: "TX",
 *     score: 150,
 *     children: [
 *       { name: "weblocationnames", value: "Dallas", score: 12 },
 *       { name: "weblocationnames", value: "San Antonio", score: 13 }
 *     ]
 *   }
 * ]
 *
 * Example Output:
 * {
 *   makes: {
 *     Ford: ["Fiesta", "F150"]
 *   },
 *   models: {},
 *   states: {
 *     TX: ["Dallas", "San Antonio"]
 *   },
 *   weblocationnames: {}
 * }
 */
const convertToParentChildMap = (data: Facet[]): ParentChildMap => {
    const map: ParentChildMap = {};

    data.forEach(parent => {
        const parentCategory = parent.name;
        const parentValue = parent.value;

        if (!map[parentCategory]) {
            map[parentCategory] = {};
        }

        if (parent.children) {
            map[parentCategory][parentValue] = parent.children.map(child => child.value); // Collect child values
            parent.children.forEach(child => {
                if (child.children) {
                    const childCategory = child.name;
                    if (!map[childCategory]) {
                        map[childCategory] = {};
                    }
                    map[childCategory][child.value] = child.children.map(
                        grandChild => grandChild.value
                    );
                }
            });
        } else {
            map[parentCategory][parentValue] = [];
        }
    });

    return map;
};

// Helper function to convert hierarchical data into a flat structure
/**
 * Converts hierarchical facet data into a flat structure.
 *
 * Example Input:
 * [
 *   {
 *     name: "makes",
 *     value: "Ford",
 *     score: 149,
 *     children: [
 *       { name: "models", value: "Fiesta", score: 10 },
 *       { name: "models", value: "F150", score: 11, }
 *     ]
 *   },
 *   {
 *     name: "states",
 *     value: "TX",
 *     score: 150,
 *     children: [
 *       { name: "weblocationnames", value: "Dallas", score: 12 },
 *       { name: "weblocationnames", value: "San Antonio", score: 13 }
 *     ]
 *   }
 * ]
 *
 * Example Output:
 * {
 *   makes: ["Ford"],
 *   models: ["Fiesta", "F150"],
 *   states: ["TX"],
 *   weblocationnames: ["Dallas", "San Antonio"]
 * }
 */
const convertToFlatStructure = (data: Facet[]): FlatStructure => {
    const result: FlatStructure = {};

    data.forEach(parent => {
        const parentCategory = parent.name; // e.g., "makes" or "states"
        const parentValue = parent.value; // e.g., "Ford" or "TX"

        if (!result[parentCategory]) {
            result[parentCategory] = [];
        }

        result[parentCategory].push(parentValue);

        if (parent.children) {
            parent.children.forEach(child => {
                const childCategory = child.name; // e.g., "models" or "weblocationnames"
                const childValue = child.value; // e.g., "Fiesta" or "Dallas"

                if (!result[childCategory]) {
                    result[childCategory] = [];
                }

                result[childCategory].push(childValue);
            });
        }
    });

    return result;
};

export function useFacetHierarchy(parentChildPairs: [string, string?][]): UseGetFacetsResult {
    const { getToken } = useAuth();

    const queries = parentChildPairs.map(([parent, child]) => {
        return {
            queryKey: ['facets', parent],
            queryFn: () => getFacets(parent, getToken, child),
            refetchOnMount: 'true',
            staleTime: 300000, // 5 minutes, we want to cache this
        } as QueryOptions<Facet[]>;
    });

    const results = useQueries({ queries });

    const isLoading = results.some(result => result.isLoading || result.isFetching);

    // Combine all results into a single array
    const combinedData = results.flatMap(result => result.data || []);

    // Generate both parentChildMap and flatStructure
    const parentChildMap = convertToParentChildMap(combinedData);
    const flatStructure = convertToFlatStructure(combinedData);

    return { parentChildMap, flatStructure, isLoading };
}
